(function () {
  'use strict';

  if (window.UnifiedSchema) {
    return;
  }

  const VERSION = '1.2.0';

  const FIELD_TYPES = {
    STRING: 'string',
    NUMBER: 'number',
    BOOLEAN: 'boolean',
    OBJECT: 'object',
    ARRAY: 'array'
  };

  const NORMALIZERS = {
    TRIM: 'trim',
    LOWERCASE: 'lowercase',
    NUMBER: 'number',
    BOOLEAN: 'boolean',
    PHONE: 'phone',
    EMAIL: 'email'
  };

  /*
   * ------------------------------------------------------------
   * CORE FIELDS
   * ------------------------------------------------------------
   */

  const coreFields = {
    ut_session_id: {
      type: FIELD_TYPES.STRING,
      required: true,
      analytics: 'bigquery',
      normalize: [
        NORMALIZERS.TRIM
      ]
    },

    page_sequence: {
      type: FIELD_TYPES.NUMBER,
      required: true,
      analytics: 'metric',
      normalize: [
        NORMALIZERS.NUMBER
      ]
    },

    page_location: {
      type: FIELD_TYPES.STRING,
      required: true,
      analytics: 'ga4_standard',
      normalize: [
        NORMALIZERS.TRIM
      ]
    },

    page_path: {
      type: FIELD_TYPES.STRING,
      required: true,
      analytics: 'ga4_standard',
      normalize: [
        NORMALIZERS.TRIM
      ]
    },

    page_title: {
      type: FIELD_TYPES.STRING,
      required: true,
      analytics: 'ga4_standard',
      normalize: [
        NORMALIZERS.TRIM
      ]
    },

    page_language: {
      type: FIELD_TYPES.STRING,
      required: false,
      analytics: 'dimension',
      normalize: [
        NORMALIZERS.TRIM,
        NORMALIZERS.LOWERCASE
      ]
    },

    page_loaded_at: {
      type: FIELD_TYPES.STRING,
      required: false,
      analytics: 'internal',
      normalize: [
        NORMALIZERS.TRIM
      ]
    },

    time_since_page_load: {
      type: FIELD_TYPES.NUMBER,
      required: false,
      analytics: 'internal',
      normalize: [
        NORMALIZERS.NUMBER
      ]
    },

    seconds_since_page_load: {
      type: FIELD_TYPES.NUMBER,
      required: true,
      analytics: 'metric',
      normalize: [
        NORMALIZERS.NUMBER
      ],
      minimum: 0
    },

    scroll_depth: {
      type: FIELD_TYPES.NUMBER,
      required: true,
      analytics: 'metric',
      normalize: [
        NORMALIZERS.NUMBER
      ],
      minimum: 0,
      maximum: 100
    },

    referrer_domain: {
      type: FIELD_TYPES.STRING,
      required: false,
      analytics: 'dimension',
      normalize: [
        NORMALIZERS.TRIM,
        NORMALIZERS.LOWERCASE
      ]
    },

    navigation_type: {
      type: FIELD_TYPES.STRING,
      required: false,
      analytics: 'dimension',
      normalize: [
        NORMALIZERS.TRIM,
        NORMALIZERS.LOWERCASE
      ]
    },

    event_timestamp: {
      type: FIELD_TYPES.STRING,
      required: true,
      analytics: 'internal',
      normalize: [
        NORMALIZERS.TRIM
      ]
    },

    event_category: {
      type: FIELD_TYPES.STRING,
      required: true,
      analytics: 'dimension',
      normalize: [
        NORMALIZERS.TRIM,
        NORMALIZERS.LOWERCASE
      ]
    },

    service: {
      type: FIELD_TYPES.STRING,
      required: true,
      analytics: 'dimension',
      normalize: [
        NORMALIZERS.TRIM,
        NORMALIZERS.LOWERCASE
      ]
    },

    conversion_candidate: {
      type: FIELD_TYPES.BOOLEAN,
      required: true,
      analytics: 'internal',
      normalize: [
        NORMALIZERS.BOOLEAN
      ]
    }
  };

  /*
   * ------------------------------------------------------------
   * REUSABLE FIELD DEFINITIONS
   * ------------------------------------------------------------
   */

  const clickLocationField = {
    type: FIELD_TYPES.STRING,
    required: true,
    analytics: 'dimension',
    normalize: [
      NORMALIZERS.TRIM,
      NORMALIZERS.LOWERCASE
    ]
  };

  const sectionNameField = {
    type: FIELD_TYPES.STRING,
    required: false,
    analytics: 'dimension',
    normalize: [
      NORMALIZERS.TRIM,
      NORMALIZERS.LOWERCASE
    ]
  };

  const ctaLabelField = {
    type: FIELD_TYPES.STRING,
    required: false,
    analytics: 'dimension',
    normalize: [
      NORMALIZERS.TRIM
    ]
  };

  /*
   * ------------------------------------------------------------
   * EVENT SCHEMAS
   * ------------------------------------------------------------
   */

  const eventSchemas = {
    /*
     * SECTION VIEW
     */

    section_view: {
      category: 'engagement',

      required: [
        'section_name',
        'section_index',
        'visibility_ratio'
      ],

      optional: [
        'section_id',
        'section_type',
        'section_source',
        'section_selector',
        'visible_ms'
      ],

      fields: {
        section_name: {
          type: FIELD_TYPES.STRING,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        section_index: {
          type: FIELD_TYPES.NUMBER,
          required: true,
          analytics: 'metric',
          normalize: [
            NORMALIZERS.NUMBER
          ],
          minimum: 0
        },

        section_id: {
          type: FIELD_TYPES.STRING,
          required: false,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM
          ]
        },

        section_type: {
          type: FIELD_TYPES.STRING,
          required: false,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        section_source: {
          type: FIELD_TYPES.STRING,
          required: false,
          analytics: 'internal',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        section_selector: {
          type: FIELD_TYPES.STRING,
          required: false,
          analytics: 'internal',
          normalize: [
            NORMALIZERS.TRIM
          ]
        },

        visibility_ratio: {
          type: FIELD_TYPES.NUMBER,
          required: true,
          analytics: 'metric',
          normalize: [
            NORMALIZERS.NUMBER
          ],
          minimum: 0,
          maximum: 1
        },

        visible_ms: {
          type: FIELD_TYPES.NUMBER,
          required: false,
          analytics: 'metric',
          normalize: [
            NORMALIZERS.NUMBER
          ],
          minimum: 0
        }
      }
    },

    /*
     * CTA CLICK
     */

    cta_click: {
      category: 'engagement',

      required: [
        'cta_label',
        'cta_target',
        'click_location'
      ],

      optional: [
        'section_name'
      ],

      fields: {
        cta_label: {
          type: FIELD_TYPES.STRING,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM
          ]
        },

        cta_target: {
          type: FIELD_TYPES.STRING,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        click_location:
          clickLocationField,

        section_name:
          sectionNameField
      }
    },

    /*
     * PHONE CLICK
     *
     * Nu trimitem numarul efectiv in payload.
     */

    phone_click: {
      category: 'lead',

      required: [
        'click_location'
      ],

      optional: [
        'cta_label',
        'section_name'
      ],

      fields: {
        click_location:
          clickLocationField,

        cta_label:
          ctaLabelField,

        section_name:
          sectionNameField
      }
    },

    /*
     * EMAIL CLICK
     *
     * Nu trimitem adresa efectiva in payload.
     */

    email_click: {
      category: 'lead',

      required: [
        'click_location'
      ],

      optional: [
        'cta_label',
        'section_name'
      ],

      fields: {
        click_location:
          clickLocationField,

        cta_label:
          ctaLabelField,

        section_name:
          sectionNameField
      }
    },

    /*
     * WHATSAPP CLICK
     *
     * Nu trimitem numarul, URL-ul sau mesajul precompletat.
     */

    whatsapp_click: {
      category: 'lead',

      required: [
        'click_location'
      ],

      optional: [
        'cta_label',
        'section_name'
      ],

      fields: {
        click_location:
          clickLocationField,

        cta_label:
          ctaLabelField,

        section_name:
          sectionNameField
      }
    },

    /*
     * CONTACT FORM
     *
     * Privacy by design:
     * doar indicatori si metadate.
     */

    contact_form_submit: {
      category: 'lead',

      required: [
        'inquiry_type',
        'has_phone',
        'has_email',
        'message_length'
      ],

      optional: [
        'has_partner_name',
        'has_event_date',
        'has_guest_count',
        'has_location',
        'has_budget',
        'form_location',
        'section_name'
      ],

      fields: {
        inquiry_type: {
          type: FIELD_TYPES.STRING,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        has_phone: {
          type: FIELD_TYPES.BOOLEAN,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.BOOLEAN
          ]
        },

        has_email: {
          type: FIELD_TYPES.BOOLEAN,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.BOOLEAN
          ]
        },

        has_partner_name: {
          type: FIELD_TYPES.BOOLEAN,
          required: false,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.BOOLEAN
          ]
        },

        has_event_date: {
          type: FIELD_TYPES.BOOLEAN,
          required: false,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.BOOLEAN
          ]
        },

        has_guest_count: {
          type: FIELD_TYPES.BOOLEAN,
          required: false,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.BOOLEAN
          ]
        },

        has_location: {
          type: FIELD_TYPES.BOOLEAN,
          required: false,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.BOOLEAN
          ]
        },

        has_budget: {
          type: FIELD_TYPES.BOOLEAN,
          required: false,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.BOOLEAN
          ]
        },

        message_length: {
          type: FIELD_TYPES.NUMBER,
          required: true,
          analytics: 'metric',
          normalize: [
            NORMALIZERS.NUMBER
          ],
          minimum: 0
        },

        form_location: {
          type: FIELD_TYPES.STRING,
          required: false,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        section_name:
          sectionNameField
      }
    },

    /*
     * NEWSLETTER
     */

    newsletter_signup: {
      category: 'nurture',

      required: [
        'form_location'
      ],

      optional: [
        'section_name'
      ],

      fields: {
        form_location: {
          type: FIELD_TYPES.STRING,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        section_name:
          sectionNameField
      }
    },

    /*
     * ARTICLE SHARE
     */

    article_share: {
      category: 'engagement',

      required: [
        'share_method',
        'click_location'
      ],

      optional: [
        'section_name'
      ],

      fields: {
        share_method: {
          type: FIELD_TYPES.STRING,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        click_location:
          clickLocationField,

        section_name:
          sectionNameField
      }
    },

    /*
     * SOCIAL CLICK
     */

    social_click: {
      category: 'engagement',

      required: [
        'social_platform',
        'click_location'
      ],

      optional: [
        'cta_label',
        'section_name'
      ],

      fields: {
        social_platform: {
          type: FIELD_TYPES.STRING,
          required: true,
          analytics: 'dimension',
          normalize: [
            NORMALIZERS.TRIM,
            NORMALIZERS.LOWERCASE
          ]
        },

        click_location:
          clickLocationField,

        cta_label:
          ctaLabelField,

        section_name:
          sectionNameField
      }
    }
  };

  /*
   * ------------------------------------------------------------
   * ALIASES
   * ------------------------------------------------------------
   */

  const aliases = {
    section:
      'section_name',

    sectionName:
      'section_name',

    sectionIndex:
      'section_index',

    sectionId:
      'section_id',

    sectionType:
      'section_type',

    sectionSource:
      'section_source',

    sectionSelector:
      'section_selector',

    visibilityRatio:
      'visibility_ratio',

    visibleMs:
      'visible_ms',

    clickLocation:
      'click_location',

    ctaLabel:
      'cta_label',

    ctaTarget:
      'cta_target',

    inquiryType:
      'inquiry_type',

    messageLength:
      'message_length',

    hasPhone:
      'has_phone',

    hasEmail:
      'has_email',

    hasPartnerName:
      'has_partner_name',

    hasEventDate:
      'has_event_date',

    hasGuestCount:
      'has_guest_count',

    hasLocation:
      'has_location',

    hasBudget:
      'has_budget',

    formLocation:
      'form_location',

    shareMethod:
      'share_method',

    socialPlatform:
      'social_platform'
  };

  /*
   * ------------------------------------------------------------
   * PUBLIC HELPERS
   * ------------------------------------------------------------
   */

  function clone(value) {
    return JSON.parse(
      JSON.stringify(value)
    );
  }

  function getEventSchema(
    eventName
  ) {
    const schema =
      eventSchemas[eventName];

    if (!schema) {
      return null;
    }

    return clone(schema);
  }

  function getFieldDefinition(
    eventName,
    fieldName
  ) {
    if (
      coreFields[fieldName]
    ) {
      return clone(
        coreFields[fieldName]
      );
    }

    const eventSchema =
      getEventSchema(
        eventName
      );

    if (
      eventSchema &&
      eventSchema.fields &&
      eventSchema.fields[
        fieldName
      ]
    ) {
      return clone(
        eventSchema.fields[
          fieldName
        ]
      );
    }

    return null;
  }

  function getRequiredFields(
    eventName
  ) {
    const eventSchema =
      getEventSchema(
        eventName
      );

    const coreRequired =
      Object.keys(
        coreFields
      ).filter(
        function (
          fieldName
        ) {
          return (
            coreFields[
              fieldName
            ].required === true
          );
        }
      );

    const eventRequired =
      eventSchema &&
      Array.isArray(
        eventSchema.required
      )
        ? eventSchema.required
        : [];

    return Array.from(
      new Set(
        coreRequired.concat(
          eventRequired
        )
      )
    );
  }

  function getAllowedFields(
    eventName
  ) {
    const eventSchema =
      getEventSchema(
        eventName
      );

    const fields =
      Object.keys(
        coreFields
      );

    if (
      eventSchema &&
      eventSchema.fields
    ) {
      fields.push.apply(
        fields,
        Object.keys(
          eventSchema.fields
        )
      );
    }

    return Array.from(
      new Set(fields)
    );
  }

  function getAlias(
    fieldName
  ) {
    return (
      aliases[fieldName] ||
      null
    );
  }

  function getAnalyticsMap(
    eventName
  ) {
    const result = {};

    getAllowedFields(
      eventName
    ).forEach(
      function (
        fieldName
      ) {
        const definition =
          getFieldDefinition(
            eventName,
            fieldName
          );

        result[fieldName] =
          definition &&
          definition.analytics
            ? definition.analytics
            : 'unclassified';
      }
    );

    return result;
  }

  function getNormalizationRules(
    eventName,
    fieldName
  ) {
    const definition =
      getFieldDefinition(
        eventName,
        fieldName
      );

    if (
      !definition ||
      !Array.isArray(
        definition.normalize
      )
    ) {
      return [];
    }

    return clone(
      definition.normalize
    );
  }

  function getNormalizationMap(
    eventName
  ) {
    const result = {};

    getAllowedFields(
      eventName
    ).forEach(
      function (
        fieldName
      ) {
        result[fieldName] =
          getNormalizationRules(
            eventName,
            fieldName
          );
      }
    );

    return result;
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedSchema = {
    version:
      VERSION,

    types:
      FIELD_TYPES,

    normalizers:
      NORMALIZERS,

    coreFields:
      clone(
        coreFields
      ),

    events:
      clone(
        eventSchemas
      ),

    aliases:
      clone(
        aliases
      ),

    getEventSchema:
      getEventSchema,

    getFieldDefinition:
      getFieldDefinition,

    getRequiredFields:
      getRequiredFields,

    getAllowedFields:
      getAllowedFields,

    getAlias:
      getAlias,

    getAnalyticsMap:
      getAnalyticsMap,

    getNormalizationRules:
      getNormalizationRules,

    getNormalizationMap:
      getNormalizationMap
  };

  if (
    window.UnifiedTrackingConfig &&
    window.UnifiedTrackingConfig.debug
  ) {
    console.info(
      '[Unified Schema] Bianchi Analytics Contract initialized',
      {
        version:
          VERSION,

        coreFields:
          Object.keys(
            coreFields
          ).length,

        events:
          Object.keys(
            eventSchemas
          ).length,

        aliases:
          Object.keys(
            aliases
          ).length,

        normalizers:
          Object.keys(
            NORMALIZERS
          ).length
      }
    );
  }
})();